import { useId, type ReactNode } from 'react';
import type { EventReviewWarning } from '../types';
import {
  clockMinutes, EVENT_TIME_ZONE, formatDateLabel, instantToEventDateTime,
} from '../lib/eventTemporal';
import { dateProofReasonText } from '../lib/dateProofReasons';
import type {
  DateProofAssessment, DateProofCandidate, DateProofCoverage, DateProofCurrentEvent,
  DateProofDetail, DateProofFields, DateProofHistoryEntry, DateProofPanelStatus,
  DateProofSource, DateProofTemporalValues, DateProofVerification,
} from '../lib/dateProofTypes';
import { EVENT_REVIEW_WARNING_META } from './EventReviewWarningChips';

export interface DateProofPanelProps {
  eventTitle: string;
  warnings?: readonly EventReviewWarning[];
  /** Server-confirmed values only. Never replace these with the pending choice. */
  current: DateProofCurrentEvent;
  detail: DateProofDetail | null;
  /** Server-ranked claims. Only distinct, server-admitted choices are shown. */
  candidates?: readonly DateProofCandidate[];
  /** Include conflicts and previous choices, not just the latest successful decision. */
  history?: readonly DateProofHistoryEntry[];
  status: DateProofPanelStatus;
  message?: string | null;
  /** The event's own source link, for when no stored source context exists. */
  sourceUrl?: string;
  /**
   * Apply immediately, to this occurrence only. The owner sets saving/single-flight,
   * catches failures, validates candidate/version on the server, and replaces
   * current/detail only after the durable receipt succeeds. No second confirmation.
   */
  onChoose: (candidateId: string, fields: DateProofFields) => void;
  onClose: () => void;
  /** Explicit read-only retry of stored event/ledger data; never an OCR/provider/model check. */
  onRefresh?: () => void;
  /** Owner-supplied actions, rendered after the evidence. */
  footer?: ReactNode;
  id?: string;
  /** Pass this to the owning dialog's aria-labelledby. */
  titleId?: string;
  className?: string;
}

const VERIFICATION_LABELS: Record<DateProofVerification, string> = {
  unverified: 'Unverified',
  conflicted: 'Conflicting sources',
  'human-confirmed': 'Confirmed by hand',
  'machine-verified': 'Verified against the source',
  'not-applicable': 'Not applicable',
};
const CHOICE_LABELS: Record<DateProofFields, string> = {
  date: 'Use this date',
  time: 'Use this time',
  both: 'Use this date and time',
};
const CHOICE_ORDER: readonly DateProofFields[] = ['date', 'time', 'both'];
const HISTORY_LABELS: Record<DateProofHistoryEntry['kind'], string> = {
  'human-confirmation': 'Human confirmation',
  'machine-verification': 'Machine verification',
  'source-conflict': 'Source conflict',
  superseded: 'Superseded evidence',
  'failed-application': 'Unsuccessful save',
};
const FIELD_LABELS: Record<DateProofFields, string> = { date: 'Date only', time: 'Time only', both: 'Date and time' };
const MAIN_OPTIONS = 2;

function dateLabel(value: string | null | undefined): string | null {
  return value ? formatDateLabel(value, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : null;
}

function dateRange(values: DateProofTemporalValues): string | null {
  const start = dateLabel(values.startDate);
  if (!start) return null;
  if (values.endDate == null) return start;
  const end = dateLabel(values.endDate);
  if (!end || values.endDate < values.startDate!) return null;
  return values.endDate === values.startDate ? start : `${start} – ${end}`;
}

function clockLabel(value: string | null | undefined): string | null {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) return null;
  const minutes = clockMinutes(value);
  if (minutes === null) return null;
  const hour = Math.floor(minutes / 60);
  return `${hour % 12 || 12}:${String(minutes % 60).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`;
}

function timeRange(values: DateProofTemporalValues): string | null {
  if (values.isAllDay === true) {
    return values.startTime == null && values.endTime == null ? 'All day' : null;
  }
  const start = clockLabel(values.startTime);
  if (!start) return null;
  if (values.endTime == null) {
    return values.endDate && values.endDate !== values.startDate ? `${start} (end time unavailable)` : start;
  }
  const end = clockLabel(values.endTime);
  if (!end) return null;
  const laterDate = dateLabel(values.startDate) && dateLabel(values.endDate)
    && values.endDate! > values.startDate!;
  if (clockMinutes(values.endTime)! < clockMinutes(values.startTime!)! && !laterDate) return null;
  return `${start} – ${end}${laterDate ? ` (ends ${dateLabel(values.endDate)})` : ''}`;
}

/** One line. A claim with no clock is date-only, not a missing time. */
function schedule(values: DateProofTemporalValues): string {
  const time = timeRange(values) ?? (values.startTime == null && values.isAllDay !== true ? null : 'Time unavailable');
  return [dateRange(values) ?? 'Date unavailable', time].filter(Boolean).join(' · ');
}

function sourceHref(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return (url.protocol === 'https:' || url.protocol === 'http:') && !url.username && !url.password ? url.href : undefined;
  } catch {
    return undefined;
  }
}

const sourceHost = (href: string) => new URL(href).hostname.replace(/^www\./, '');

function SourceLink({ source, eventTitle }: { source: Partial<DateProofSource>; eventTitle: string }) {
  const href = sourceHref(source.sourceUrl);
  if (!href) return <span className="mh-date-proof__muted">Source link unavailable.</span>;
  const host = sourceHost(href);
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      aria-label={`View source ${source.sourceLabel?.trim() || host} for ${eventTitle} (opens in a new tab)`}>
      {host} <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
    </a>
  );
}

function SourceText({ source, eventTitle }: { source: Partial<DateProofSource>; eventTitle: string }) {
  return (
    <div className="mh-date-proof__source">
      <SourceLink source={source} eventTitle={eventTitle} />
      {source.literalQuote?.trim() && <blockquote className="mh-date-proof__quote"><p>{source.literalQuote}</p></blockquote>}
    </div>
  );
}

function Coverage({ coverage }: { coverage: DateProofCoverage | undefined }) {
  if (!coverage || coverage.status === 'complete') return null;
  const label = coverage.status === 'partial' ? 'Partial coverage' : 'Coverage unavailable';
  return <p className="mh-date-proof__muted mb-0"><strong>{label}.</strong> {coverage.detail}</p>;
}

function Assessment({ field, warning, assessment }: {
  field: 'date' | 'time'; warning: boolean; assessment: DateProofAssessment | undefined;
}) {
  const meta = EVENT_REVIEW_WARNING_META[field === 'date' ? 'check-date' : 'time-unverified'];
  const settled = assessment?.status === 'conflicted' || assessment?.status === 'human-confirmed'
    || assessment?.status === 'machine-verified';
  if (!warning && !settled) return null;
  const heading = warning ? meta.label : `${field === 'date' ? 'Date' : 'Time'}: ${VERIFICATION_LABELS[assessment!.status]}`;
  const reason = dateProofReasonText(assessment?.reason) ?? (warning ? meta.tooltip : null);
  return (
    <div className="mh-date-proof__assessment">
      <h3 className="h6 mb-1">{heading}</h3>
      {reason && <p className="mb-0">{reason}</p>}
    </div>
  );
}

function Candidate({ candidate, eventTitle, status, onChoose, id }: {
  candidate: DateProofCandidate; eventTitle: string; status: DateProofPanelStatus;
  onChoose: DateProofPanelProps['onChoose']; id: string;
}) {
  const choices = CHOICE_ORDER.filter((field) => candidate.allowedFields.includes(field));
  const hasSource = Boolean(candidate.sourceLabel?.trim() && candidate.literalQuote?.trim())
    && candidate.coverage.status !== 'unavailable';
  const dateAvailable = dateRange(candidate.values) !== null;
  const timeAvailable = timeRange(candidate.values) !== null;
  const reason = dateProofReasonText(candidate.reason);
  return (
    <li className="mh-date-proof__candidate">
      <p id={`${id}-values`} className="fw-semibold mb-1">{schedule(candidate.values)}</p>
      {candidate.literalQuote?.trim() && (
        <blockquote id={`${id}-source`} className="mh-date-proof__quote mh-date-proof__quote--short"><p>{candidate.literalQuote}</p></blockquote>
      )}
      <p className="mb-2"><SourceLink source={candidate} eventTitle={eventTitle} /></p>
      {reason && <p id={`${id}-reason`} className="mh-date-proof__muted">{reason}</p>}
      {(!hasSource || !dateAvailable && !timeAvailable) && (
        <p className="mh-date-proof__muted">This option can&apos;t be applied.</p>
      )}
      <div className="mh-date-proof__actions">
        {choices.map((field) => {
          const validValues = field === 'date' ? dateAvailable : field === 'time' ? timeAvailable : dateAvailable && timeAvailable;
          const disabled = status !== 'ready' || !candidate.id.trim() || !hasSource || !validValues;
          return (
            <button key={field} type="button" className="btn btn-outline-secondary"
              disabled={disabled}
              aria-label={`${CHOICE_LABELS[field]} for ${eventTitle} from ${candidate.sourceLabel || 'this source'}. This occurrence only.`}
              aria-describedby={`${id}-values ${id}-source${reason ? ` ${id}-reason` : ''}`}
              onClick={(event) => {
                event.stopPropagation();
                if (!disabled) onChoose(candidate.id, field);
              }}>
              {CHOICE_LABELS[field]}
            </button>
          );
        })}
      </div>
    </li>
  );
}

function HistoryEntry({ entry, eventTitle }: { entry: DateProofHistoryEntry; eventTitle: string }) {
  const instant = instantToEventDateTime(entry.recordedAt);
  const recorded = instant.ok ? new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium', timeStyle: 'short', timeZone: EVENT_TIME_ZONE,
  }).format(new Date(instant.value.isoUtc)) : null;
  const changed = entry.before && entry.after && schedule(entry.before) !== schedule(entry.after);
  return (
    <li className="mh-date-proof__history-entry">
      <h4 className="h6 mb-1">{HISTORY_LABELS[entry.kind]} · {FIELD_LABELS[entry.fields]}</h4>
      <p className="mh-date-proof__muted mb-1">
        <time dateTime={instant.ok ? instant.value.isoUtc : undefined}>{recorded ?? 'Record time unavailable'}</time>
      </p>
      <p className="mb-1">{entry.summary}</p>
      {changed && <p className="mb-1">Before: {schedule(entry.before!)}<br />After: {schedule(entry.after!)}</p>}
      {(entry.sourceLabel || entry.literalQuote || entry.sourceUrl) && <SourceText source={entry} eventTitle={eventTitle} />}
    </li>
  );
}

/**
 * The ledger keeps every claim. Only distinct choices the server admits are worth
 * a row, and a date-only claim adds nothing beside a timed one offering that date.
 */
function distinctOptions(candidates: readonly DateProofCandidate[]): DateProofCandidate[] {
  const seen = new Set<string>();
  const options = candidates.filter((candidate) => {
    if (!candidate.allowedFields.length) return false;
    const key = JSON.stringify([schedule(candidate.values), [...candidate.allowedFields].sort()]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const timedDates = new Set(options.filter((option) => option.values.startTime && option.allowedFields.includes('date'))
    .map((option) => dateRange(option.values)));
  return options.filter((option) => option.values.startTime || !timedDates.has(dateRange(option.values)));
}

/** One link per site; every source stays readable under Source text. The event link is the last resort. */
function sourceLinks(sources: readonly DateProofSource[], fallbackUrl: string | undefined): Partial<DateProofSource>[] {
  const links = new Map<string, Partial<DateProofSource>>();
  for (const source of sources) {
    const href = sourceHref(source.sourceUrl);
    if (href && !links.has(sourceHost(href))) links.set(sourceHost(href), source);
  }
  return links.size ? [...links.values()] : sourceHref(fallbackUrl) ? [{ sourceUrl: fallbackUrl }] : [];
}

/**
 * Normal dialog BODY, not an overlay. The owner mounts it outside clipped event
 * lists and owns focus trapping/return, Escape/backdrop handling (including the
 * saving guard), authentication, refresh, and persistence. Rendering is read-only.
 */
export default function DateProofPanel({
  eventTitle, warnings, current, detail, candidates = [], history = [], status, message, sourceUrl,
  onChoose, onClose, onRefresh, footer, id, titleId, className = '',
}: DateProofPanelProps) {
  const generatedId = useId();
  const panelId = id || `mh-date-proof-${generatedId}`;
  const headingId = titleId || `${panelId}-title`;
  const busy = status === 'loading' || status === 'saving';
  const failed = status === 'stale' || status === 'error';
  const sources = detail?.sources ?? [];
  const links = sourceLinks(sources, sourceUrl);
  const quoted = sources.filter((source) => source.literalQuote?.trim());
  const options = distinctOptions(candidates);
  const notices: Record<Exclude<DateProofPanelStatus, 'ready'>, string> = {
    loading: 'Loading…',
    saving: 'Saving…',
    stale: 'This event changed. Try again before choosing.',
    error: "Couldn't load the source details.",
  };

  return (
    <section id={panelId} className={`mh-date-proof${className ? ` ${className}` : ''}`}
      aria-labelledby={headingId} aria-describedby={`${panelId}-saved`}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}>
      <header className="mh-date-proof__header">
        <div>
          <h2 id={headingId} className="h5 mb-1"><span className="visually-hidden">Date and time review for </span>{eventTitle}</h2>
          <p id={`${panelId}-saved`} className="mh-date-proof__saved mb-0">{schedule(current)}</p>
        </div>
        <button type="button" className="btn btn-outline-secondary" disabled={status === 'saving'}
          aria-label={`Close date and time review for ${eventTitle}`} onClick={onClose}>Close</button>
      </header>
      {busy && <p className="mh-date-proof__muted" role="status" aria-atomic="true">{message || notices[status]}</p>}
      {failed && (
        <div className="alert alert-warning mh-date-proof__notice" role="alert" aria-atomic="true">
          <p className="mb-0">{message || notices[status]}</p>
          {onRefresh && <button type="button" className="btn btn-outline-secondary mt-2" onClick={onRefresh}>Try again</button>}
        </div>
      )}
      {current.wrongDateFlag && (
        <div className="alert alert-danger mh-date-proof__flag">
          <strong><i className="bi bi-flag me-1" aria-hidden="true" />Wrong-date flag</strong>
          <p className="mb-0">Choosing a date or time does not clear it.</p>
        </div>
      )}
      <div aria-busy={busy}>
        <Assessment field="date" warning={warnings?.includes('check-date') ?? false} assessment={detail?.date} />
        <Assessment field="time" warning={warnings?.includes('time-unverified') ?? false} assessment={detail?.time} />
        {!!links.length && (
          <p className="mh-date-proof__links">
            <strong>Source</strong>
            {links.map((source, index) => <SourceLink key={index} source={source} eventTitle={eventTitle} />)}
          </p>
        )}
        {options.length ? (
          <>
            <h3 className="h6 mt-3 mb-1">Options from the source</h3>
            <p className="mh-date-proof__muted mb-0">Choosing one saves immediately for this occurrence only.</p>
            <ol className="mh-date-proof__candidates">
              {options.slice(0, MAIN_OPTIONS).map((candidate, index) => (
                <Candidate key={candidate.id} candidate={candidate} eventTitle={eventTitle} status={status}
                  onChoose={onChoose} id={`${panelId}-candidate-${index}`} />
              ))}
            </ol>
            {options.length > MAIN_OPTIONS && (
              <details className="mh-date-proof__more">
                <summary>More options ({options.length - MAIN_OPTIONS})</summary>
                <ol className="mh-date-proof__candidates">
                  {options.slice(MAIN_OPTIONS).map((candidate, index) => (
                    <Candidate key={candidate.id} candidate={candidate} eventTitle={eventTitle} status={status}
                      onChoose={onChoose} id={`${panelId}-candidate-${index + MAIN_OPTIONS}`} />
                  ))}
                </ol>
              </details>
            )}
          </>
        ) : detail && <p className="mh-date-proof__muted">No usable dates found.</p>}
        {!!quoted.length && (
          <details className="mh-date-proof__more">
            <summary>Source text ({quoted.length})</summary>
            {quoted.map((source, index) => <SourceText key={index} source={source} eventTitle={eventTitle} />)}
            <Coverage coverage={detail?.coverage} />
          </details>
        )}
        {!!history.length && (
          <details className="mh-date-proof__more">
            <summary>History ({history.length})</summary>
            <ol className="mh-date-proof__history-list">
              {history.map((entry) => <HistoryEntry key={entry.id} entry={entry} eventTitle={eventTitle} />)}
            </ol>
          </details>
        )}
      </div>
      {footer}
    </section>
  );
}
